/* eslint-disable @typescript-eslint/no-namespace */

namespace Hicom.Engine.UI.TagHelper {

    /**
    * The HicomImageAnnotation class is used to manage the js for the image annotation tool. 
    */
    export class HicomImageAnnotation {

        public AllAnnotations: DataModel.AllImageAnnotationsDM;
        public CurrentAnnotationNumber: number;
        public AnnotationCanvasCtrl: HTMLCanvasElement;
        public ImageCanvasCtrl: HTMLCanvasElement;
        public AnnotationContext: CanvasRenderingContext2D;
        public ImageContext: CanvasRenderingContext2D;
        public CurrentPathPoints: Array<DataModel.PositionDM>;
        public PreviousPathPoint: DataModel.PositionDM;
        public InflightPathPoint: DataModel.PositionDM;
        public IsDrawing: boolean;
        public ImgSrc: string;
        public CtrlOutterId: string;
        public ClickDrawingFunc: (event: PointerEvent) => void;
        public DblClickDrawingFunc: (event: MouseEvent) => void;
        public MouseDownDrawingFunc: (event: MouseEvent) => void;
        public MouseMoveDrawingFunc: (event: MouseEvent) => void;
        public MouseUpDrawingFunc: (event: MouseEvent) => void;
        public MouseMoveMarkerTextFunc: (event: MouseEvent) => void;
        public TouchStartFunc: (event: TouchEvent) => void;
        public PolyButtonClickFunc: (event: PointerEvent) => void;
        public CurrentMarkerType: Hicom.Engine.UI.TagHelper.AnnotationMarkerTypeEnum;
        public MarkerLineThickness: number;
        public MarkerLineColour: string;
        public MarkerLineColourCssClass: string;
        public MarkerFillColour: string;
        public MarkerFillColourCssClass: string;
        public MarkerHighlightLineColour: string;
        public MarkerHighlightLineColourCssClass: string;
        public MarkerHighlightFillColour: string;
        public MarkerHighlightFillColourCssClass: string;
        public AnnotationMarkerColour: string;
        public AnnotationMarkerColourCssClass: string;
        public AnnotationTextColour: string;
        public AnnotationTextColourCssClass: string;
        public AnnotationsDisplayMode: Hicom.Engine.UI.TagHelper.AnnotationsDisplayModeEnum;
        public ReadOnlyMode: boolean;
        public ToolTipCtrl: HTMLElement;
        public CurrentToolTipText: string;
        public ResxRemoveAnnotationTitle: string;
        public ResxRemoveAnnotationContent: string;
        public HighlightingToClear: boolean;
        public AnnotationListLocation: Hicom.Engine.UI.TagHelper.AnnotationListLocationEnum;
        public InTouchMode: boolean;
        public InPolyDrawing: boolean;
        public ForceWidth: number;
        public ForceHeight: number;
        public CanvasImagePartialID: string;
        public CanvasAnnotationPartialID: string;
        public HiddenAnnoJsonHolder: string;
        public TempAnnotationCanvasCtrl: HTMLCanvasElement;
        public TempAnnotationContext: CanvasRenderingContext2D;

        /**
         * The constructor of the HicomImageAnnotation class.
         * @param outterCtrlId - The ID of the outer control.
         * @param markerLineThickness - The thickness of the marker line.
         * @param markerLineColourCssClass - The CSS class for the color of the marker line.
         * @param markerFillColourCssClass - The CSS class for the color of the marker fill.
         * @param markerHighlightLineColourCssClass - The CSS class for the color of the marker highlight line.
         * @param markerHighlightFillColourCssClass - The CSS class for the color of the marker highlight fill.
         * @param annotationMarkerColourCssClass - The CSS class for the color of the annotation marker.
         * @param annotationTextColourCssClass - The CSS class for the color of the annotation text.
         * @param annotationsDisplayMode - The display mode of the annotations.
         * @param readOnlyMode - Indicates if the annotation tool is in read-only mode.
         * @param resxRemoveAnnotationTitle - The title for removing an annotation.
         * @param resxRemoveAnnotationContent - The content for removing an annotation.
         * @param annotationListLocation - The location of the annotation list.                  
         * @param markerType - The type of marker.
         * @param forceHeight - The forced height of the canvas.
         * @param forceWidth - The forced width of the canvas.
         * @param canvasImagePartialID - The canvasImagePartialID.
         * @param canvasAnnotationPartialID - The canvasAnnotationPartialID.
         * @param hiddenAnnoJsonHolder - The hiddenAnnoJsonHolder.
         */
        constructor(
            outterCtrlId: string,
            markerLineThickness: number,
            markerLineColourCssClass: string,
            markerFillColourCssClass: string,
            markerHighlightLineColourCssClass: string,
            markerHighlightFillColourCssClass: string,
            annotationMarkerColourCssClass: string,
            annotationTextColourCssClass: string,
            annotationsDisplayMode: AnnotationsDisplayModeEnum,
            readOnlyMode: boolean,
            resxRemoveAnnotationTitle: string,
            resxRemoveAnnotationContent: string,
            annotationListLocation: AnnotationListLocationEnum,
            markerType: string,
            forceHeight: number = 0,
            forceWidth: number = 0,
            canvasImagePartialID: string,
            canvasAnnotationPartialID: string,
            hiddenAnnoJsonHolder: string,
        ) {

            this.AllAnnotations = new DataModel.AllImageAnnotationsDM();
            this.CurrentPathPoints = new Array<DataModel.PositionDM>();
            this.MarkerLineThickness = markerLineThickness
            this.MarkerLineColourCssClass = markerLineColourCssClass;
            this.MarkerFillColourCssClass = markerFillColourCssClass;
            this.MarkerHighlightLineColourCssClass = markerHighlightLineColourCssClass;
            this.MarkerHighlightFillColourCssClass = markerHighlightFillColourCssClass;
            this.AnnotationMarkerColourCssClass = annotationMarkerColourCssClass;
            this.AnnotationTextColourCssClass = annotationTextColourCssClass;
            this.AnnotationsDisplayMode = annotationsDisplayMode;
            this.ReadOnlyMode = readOnlyMode;
            this.ResxRemoveAnnotationTitle = resxRemoveAnnotationTitle;
            this.ResxRemoveAnnotationContent = resxRemoveAnnotationContent;
            this.AnnotationListLocation = annotationListLocation;
            this.CanvasImagePartialID = canvasImagePartialID;
            this.CanvasAnnotationPartialID = canvasAnnotationPartialID;
            this.HiddenAnnoJsonHolder = hiddenAnnoJsonHolder;

            this.ImageCanvasCtrl = <HTMLCanvasElement>document.getElementById(`${this.CanvasImagePartialID}${outterCtrlId}`);
            this.AnnotationCanvasCtrl = <HTMLCanvasElement>document.getElementById(`${this.CanvasAnnotationPartialID}${outterCtrlId}`);
            this.ToolTipCtrl = <HTMLElement>document.getElementById(`iTooltip_${outterCtrlId}`);
            this.CtrlOutterId = outterCtrlId;

            let rect: DOMRect = this.AnnotationCanvasCtrl.getBoundingClientRect();

            this.AllAnnotations.ImageWidth = rect.width
            this.AllAnnotations.ImageHeight = rect.height;

            this.CurrentAnnotationNumber = 0;
            this.CurrentToolTipText = "";
            this.HighlightingToClear = false;
            this.InTouchMode = false;
            this.InPolyDrawing = false;
            this.CurrentMarkerType = +markerType;
            this.ForceWidth = forceWidth;
            this.ForceHeight = forceHeight;

            // Extract the active theme colours from the provided CSS classes.
            this.setActiveThemeColoursFromCssClasses();
            // Handle theme changes when the theme selector is present.
            this.handleThemeChanges('themeSelect', this);
        }

        /**
         * Sets the active theme colors from the provided CSS classes.
         */
        private setActiveThemeColoursFromCssClasses() {
            this.MarkerLineColour = this.getCSSPropertiesByClassName(this.MarkerLineColourCssClass, ['background'])?.background;
            this.MarkerFillColour = this.getCSSPropertiesByClassName(this.MarkerFillColourCssClass, ['background'])?.background;
            this.MarkerHighlightLineColour = this.getCSSPropertiesByClassName(this.MarkerHighlightLineColourCssClass, ['background'])?.background;
            this.MarkerHighlightFillColour = this.getCSSPropertiesByClassName(this.MarkerHighlightFillColourCssClass, ['background'])?.background;
            this.AnnotationMarkerColour = this.getCSSPropertiesByClassName(this.AnnotationMarkerColourCssClass, ['background'])?.background;
            this.AnnotationTextColour = this.getCSSPropertiesByClassName(this.AnnotationTextColourCssClass, ['background'])?.background;
        }

        /**
         * Retrieves specified CSS properties for a given class name.
         * @param className - The class name to search for.
         * @param properties - The list of CSS properties to retrieve.
         * @returns An object containing the specified CSS properties and their values, or null if not found.
         */
        private getCSSPropertiesByClassName(className: string, properties: string[]): Record<string, string> | null {
            const styleSheets: StyleSheetList = document.styleSheets;

            for (let i = 0; i < styleSheets.length; i++) {
                const sheet = styleSheets[i] as CSSStyleSheet;

                try {
                    const rules = sheet.cssRules || sheet.rules;
                    for (let j = 0; j < rules.length; j++) {
                        const rule = rules[j] as CSSStyleRule;

                        if (rule.selectorText === `.${className}`) {
                            const result: Record<string, string> = {};

                            properties.forEach((property) => {
                                const value = rule.style.getPropertyValue(property);
                                if (value) {
                                    result[property] = value.trim(); // Trim to remove unnecessary whitespaces
                                }
                            });

                            return Object.keys(result).length > 0 ? result : null;
                        }
                    }
                } catch (error) {
                    console.warn(`Could not access rules in stylesheet: ${sheet.href}`, error);
                }
            }

            return null; // Class not found or properties not found
        }

        /**
         * Initializes the image annotation canvas.
         * @param canvasImgSrc - The source of the canvas image. (i.e. data:image/png;base64,...)
         */
        public InitImgAnnoCanvas(
            canvasImgSrc: string) {
            let imgAnnClass: HicomImageAnnotation = this;
            imgAnnClass.AnnotationContext = this.AnnotationCanvasCtrl.getContext("2d");
            imgAnnClass.ImageContext = this.ImageCanvasCtrl.getContext("2d");

            //Clear out the annotations list incase there are others from previous load of the image annotation tool.
            imgAnnClass.removeAllAnnotationsFromList(imgAnnClass);

            let img = new Image();
            img.onload = function () {

                let canvasHolderDiv: HTMLDivElement = <HTMLDivElement>imgAnnClass.AnnotationCanvasCtrl.parentElement;
                let ratioHeight: number;
                let ratioWidth: number;

                if (imgAnnClass.ForceHeight == 0 && imgAnnClass.ForceWidth == 0 && (img.height > 1000 || img.width > 1000)) {
                    // If no force has been set, we will limit the size so that the largest dimension is set at 1000px.
                    if (img.height > img.width) {
                        // Height is greater, ratio 1000 on height.
                        ratioWidth = (1000 / img.height) * img.width;
                        ratioHeight = 1000;
                    } else {
                        // Width is greater, ratio 1000 on width.
                        ratioHeight = (1000 / img.width) * img.height;
                        ratioWidth = 1000;
                    }
                }
                else {
                    ratioWidth = img.width;
                    ratioHeight = img.height;
                }

                //Define a width/height of the canvas so it renders at the right resolution!
                if (imgAnnClass.ForceHeight > 0) {
                    imgAnnClass.AnnotationCanvasCtrl.height = imgAnnClass.ForceHeight;
                    imgAnnClass.ImageCanvasCtrl.height = imgAnnClass.ForceHeight;
                    canvasHolderDiv.style.height = `${imgAnnClass.ForceHeight}px`;
                    canvasHolderDiv.style.setProperty(`--image-height`, `${img.width}px`);
                } else {
                    imgAnnClass.AnnotationCanvasCtrl.height = ratioHeight;
                    imgAnnClass.ImageCanvasCtrl.height = ratioHeight;
                    canvasHolderDiv.style.height = `${ratioHeight}px`;
                    canvasHolderDiv.style.setProperty(`--image-height`, `${ratioHeight}px`);
                }

                if (imgAnnClass.ForceWidth > 0) {
                    imgAnnClass.AnnotationCanvasCtrl.width = imgAnnClass.ForceWidth;
                    imgAnnClass.ImageCanvasCtrl.width = imgAnnClass.ForceWidth;
                    canvasHolderDiv.style.width = `${imgAnnClass.ForceWidth}px`;
                    canvasHolderDiv.style.setProperty(`--image-width`, `${img.width}px`);
                } else {

                    //This is done because if the image itself is less than 550 pixels wide the annotation canvas will only fill that space, meaning that the user will not be able to draw in that area even though it looks like they should be able to esspeically if the image backgroun matches the screen background.
                    if (ratioWidth < 550) {
                        imgAnnClass.AnnotationCanvasCtrl.width = 550;
                        canvasHolderDiv.style.width = `550px`;
                        canvasHolderDiv.style.setProperty(`--image-width`, `550px`);
                    }
                    else {
                        imgAnnClass.AnnotationCanvasCtrl.width = ratioWidth;
                        canvasHolderDiv.style.width = `${ratioWidth}px`;
                        canvasHolderDiv.style.setProperty(`--image-width`, `${ratioWidth}px`);
                    }

                    imgAnnClass.ImageCanvasCtrl.width = ratioWidth;
                }

                let hdnImgAnnoJsonHoler: HTMLInputElement = <HTMLInputElement>document.getElementById(`${imgAnnClass.HiddenAnnoJsonHolder}${imgAnnClass.CtrlOutterId}`);

                imgAnnClass.ImageContext.drawImage(img, 0, 0, img.width, img.height, // source rectangle
                    0, 0, imgAnnClass.ImageCanvasCtrl.width, imgAnnClass.ImageCanvasCtrl.height); // destination rectangle

                if (hdnImgAnnoJsonHoler.value.length > 0) {

                    //Load the Json passed in from the hidden field, in to the AllAnnotations property.
                    imgAnnClass.AllAnnotations = JSON.parse(hdnImgAnnoJsonHoler.value) as DataModel.AllImageAnnotationsDM;

                    let currentAnnotationNumber: number = 0;

                    for (var i = 0; i < imgAnnClass.AllAnnotations.Annotations.length; i++) {
                        if (imgAnnClass.AllAnnotations.Annotations[i].AnnotationNumber > currentAnnotationNumber) {
                            currentAnnotationNumber = imgAnnClass.AllAnnotations.Annotations[i].AnnotationNumber;
                        }
                    }

                    imgAnnClass.CurrentAnnotationNumber = currentAnnotationNumber;

                    imgAnnClass.resetImgAnnClass(imgAnnClass, true);

                    imgAnnClass.addAllAnnotationsToList(imgAnnClass);
                }

                // Create a temporary canvas for the annotations so that they can be drawn on top of the image without affecting the other annotations.
                imgAnnClass.TempAnnotationCanvasCtrl = document.createElement('canvas');
                imgAnnClass.TempAnnotationCanvasCtrl.width = imgAnnClass.AnnotationCanvasCtrl.width;
                imgAnnClass.TempAnnotationCanvasCtrl.height = imgAnnClass.AnnotationCanvasCtrl.height;
                imgAnnClass.TempAnnotationCanvasCtrl.style.position = 'absolute';
                imgAnnClass.TempAnnotationCanvasCtrl.style.top = '0';
                imgAnnClass.TempAnnotationCanvasCtrl.style.left = '0';
                imgAnnClass.TempAnnotationCanvasCtrl.style.pointerEvents = 'none';
                imgAnnClass.AnnotationCanvasCtrl.parentElement.appendChild(imgAnnClass.TempAnnotationCanvasCtrl);
                imgAnnClass.TempAnnotationContext = imgAnnClass.TempAnnotationCanvasCtrl.getContext('2d');
            };

            img.src = canvasImgSrc;
            this.ImgSrc = canvasImgSrc;

            //Get the div that contains the buttons.
            let divImgButtonContainer: HTMLDivElement = <HTMLDivElement>document.getElementById(`divImageAnnoBtnContainer_${imgAnnClass.CtrlOutterId}`);

            for (var i = 0; i < divImgButtonContainer.children.length; i++) {
                if (divImgButtonContainer.children[i] instanceof HTMLButtonElement) {
                    if (divImgButtonContainer.children[i].id == `btnImageAnno_Poly_${imgAnnClass.CtrlOutterId}`) {
                        imgAnnClass.PolyButtonClickFunc = function (e: PointerEvent) {
                            let btn: HTMLButtonElement = <HTMLButtonElement>this;

                            let drawingTypeOnCtrl: string = btn.getAttribute("data-marker-type");

                            imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);

                            // Check if the image annotation tool is currently in drawing mode and reset the state if it is
                            if (imgAnnClass.IsDrawing) {
                                imgAnnClass.resetImgAnnClass(imgAnnClass, true);
                            }

                            //Magic unary (+) operator for converting strings to numbers
                            let clickedMarkerType: number = +drawingTypeOnCtrl;
                            imgAnnClass.CurrentMarkerType = clickedMarkerType;

                            imgAnnClass.setMarkerType(btn);

                            imgAnnClass.setMarkerHandlers(imgAnnClass);
                        }

                        //Assign the poly button click to a function as it will be needed when this click event is changed based
                        divImgButtonContainer.children[i].addEventListener("click", imgAnnClass.PolyButtonClickFunc);
                    }
                    else {
                        divImgButtonContainer.children[i].addEventListener("click", function (event: PointerEvent) {
                            let btn: HTMLButtonElement = <HTMLButtonElement>this;

                            let drawingTypeOnCtrl: string = btn.getAttribute("data-marker-type");

                            imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);

                            // Check if the image annotation tool is currently in drawing mode and reset the state if it is
                            if (imgAnnClass.IsDrawing) {
                                imgAnnClass.resetImgAnnClass(imgAnnClass, true);
                            }

                            //Magic unary (+) operator for converting strings to numbers
                            let clickedMarkerType: number = +drawingTypeOnCtrl;
                            imgAnnClass.CurrentMarkerType = clickedMarkerType;

                            imgAnnClass.setMarkerType(btn);

                            imgAnnClass.setMarkerHandlers(imgAnnClass);
                        });
                    }

                    let btn: HTMLButtonElement = <HTMLButtonElement>divImgButtonContainer.children[i];

                    let drawingTypeOnCtrl: string = btn.getAttribute("data-marker-type");

                    if (+drawingTypeOnCtrl == imgAnnClass.CurrentMarkerType) {
                        imgAnnClass.setMarkerType(btn);
                    }
                }
            }

            if (imgAnnClass.ReadOnlyMode) {
                let btnRow: HTMLDivElement = <HTMLDivElement>document.getElementById(`divImageAnnoBtnContainer_${imgAnnClass.CtrlOutterId}`);
                btnRow.style.display = "none";

                this.setupCurriedMarkerHandlersOnceEvenInReadOnly(imgAnnClass);
                this.setReadonlyMarkerHandlers(imgAnnClass);

            } else {
                this.setupCurriedMarkerHandlersOnce(imgAnnClass);

                //Magic unary (+) operator for converting strings to numbers
                this.setMarkerHandlers(imgAnnClass);
            }
        }

        /**
         * Sets up curried marker handlers once.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private setupCurriedMarkerHandlersOnce(imgAnnClass: HicomImageAnnotation) {
            //Setup all mouse functions for the canvas, depending on the marker type.
            imgAnnClass.ClickDrawingFunc = function (event: PointerEvent) {
                if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Point) {
                    imgAnnClass.addSinglePoint(event, imgAnnClass);
                }
                else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Poly) {
                    imgAnnClass.addMultipleJoinedPoints(event, imgAnnClass);
                }
            }

            imgAnnClass.DblClickDrawingFunc = function (event: MouseEvent) {
                if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Poly) {
                    imgAnnClass.closeMultiPointPath(event, imgAnnClass);
                }
            }

            imgAnnClass.MouseDownDrawingFunc = function (event: MouseEvent) {
                if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Freehand) {
                    imgAnnClass.startFreeHandJoinedPoints(event, imgAnnClass);
                } else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Arrow) {
                    imgAnnClass.startArrowPoint(event, imgAnnClass);
                } else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Circle) {
                    imgAnnClass.startCirclePoint(event, imgAnnClass);
                }
            }

            imgAnnClass.MouseMoveDrawingFunc = function (event: MouseEvent) {
                if (imgAnnClass.IsDrawing) {
                    if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Freehand) {
                        imgAnnClass.moveFreeHandJoinedPoints(event, imgAnnClass);
                    } else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Arrow) {
                        imgAnnClass.moveArrowJoinedPoints(event, imgAnnClass);
                    } else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Circle) {
                        imgAnnClass.moveCircleJoinedPoints(event, imgAnnClass);
                    }
                }
                else {
                    imgAnnClass.checkForAnnotationMarkerHit(imgAnnClass, event);
                }
            }

            imgAnnClass.MouseUpDrawingFunc = function (event: MouseEvent) {
                if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Freehand) {
                    imgAnnClass.endFreeHandJoinedPoints(event, imgAnnClass);
                } else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Arrow) {
                    imgAnnClass.endArrowPoint(event, imgAnnClass);
                } else if (imgAnnClass.CurrentMarkerType == AnnotationMarkerTypeEnum.Circle) {
                    imgAnnClass.endCirclePoint(event, imgAnnClass);
                }
            }

            imgAnnClass.TouchStartFunc = function (event: TouchEvent) {
                imgAnnClass.touchModeON(imgAnnClass);
            }

            imgAnnClass.setupCurriedMarkerHandlersOnceEvenInReadOnly(imgAnnClass);
        }

        /**
         * Sets up curried marker handlers once even in read-only mode.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private setupCurriedMarkerHandlersOnceEvenInReadOnly(imgAnnClass: HicomImageAnnotation) {
            imgAnnClass.MouseMoveMarkerTextFunc = function (event: MouseEvent) {
                if (imgAnnClass.IsDrawing == false) {
                    imgAnnClass.checkForAnnotationMarkerHit(imgAnnClass, event);
                }
            }
        }

        /**
         * Sets the marker handlers.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private setMarkerHandlers(imgAnnClass: HicomImageAnnotation) {
            //Remove any of the functions from the event listeners.
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("click", imgAnnClass.ClickDrawingFunc);
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("dblclick", imgAnnClass.DblClickDrawingFunc);
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("mousedown", imgAnnClass.MouseDownDrawingFunc);
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("mousemove", imgAnnClass.MouseMoveDrawingFunc);
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("mouseup", imgAnnClass.MouseUpDrawingFunc);
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("mousemove", imgAnnClass.MouseMoveMarkerTextFunc);
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("touchstart", imgAnnClass.TouchStartFunc);

            //Add the correct event listeners back in depending on the marker type.
            switch (imgAnnClass.CurrentMarkerType) {
                case AnnotationMarkerTypeEnum.Point:
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("click", imgAnnClass.ClickDrawingFunc);
                    break;
                case AnnotationMarkerTypeEnum.Arrow:
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousedown", imgAnnClass.MouseDownDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousemove", imgAnnClass.MouseMoveDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mouseup", imgAnnClass.MouseUpDrawingFunc);

                    break;
                case AnnotationMarkerTypeEnum.Poly:
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("click", imgAnnClass.ClickDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("dblclick", imgAnnClass.DblClickDrawingFunc);

                    break;
                case AnnotationMarkerTypeEnum.Freehand:
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousedown", imgAnnClass.MouseDownDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousemove", imgAnnClass.MouseMoveDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mouseup", imgAnnClass.MouseUpDrawingFunc);

                    break;
                case AnnotationMarkerTypeEnum.Circle:
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousedown", imgAnnClass.MouseDownDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousemove", imgAnnClass.MouseMoveDrawingFunc);
                    imgAnnClass.AnnotationCanvasCtrl.addEventListener("mouseup", imgAnnClass.MouseUpDrawingFunc);

                    break;
                default:
                    throw new DOMException("Marker type has not been selected.");
            }

            document.addEventListener("touchstart", imgAnnClass.TouchStartFunc);

            imgAnnClass.setReadonlyMarkerHandlers(imgAnnClass);
        }

        /**
         * Sets the read-only marker handlers.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private setReadonlyMarkerHandlers(imgAnnClass: HicomImageAnnotation) {
            //Remove any of the functions from the event listeners.
            imgAnnClass.AnnotationCanvasCtrl.removeEventListener("mousemove", imgAnnClass.MouseMoveMarkerTextFunc);

            imgAnnClass.AnnotationCanvasCtrl.addEventListener("mousemove", imgAnnClass.MouseMoveMarkerTextFunc);
        }

        /**
         * Sets the marker type.
         * @param btn - The button element.
         */
        private setMarkerType(btn: HTMLButtonElement) {
            let divImgButtonContainer: HTMLDivElement = <HTMLDivElement>btn.parentElement;

            if (divImgButtonContainer) {
                for (var i = 0; i < divImgButtonContainer.children.length; i++) {
                    divImgButtonContainer.children[i].classList.remove("active");
                }
            }

            btn.classList.add("active");
        }

        /**
         * Handle when the image annotation tool goes in to touch mode.
         * @param imgAnnClass
         */
        private touchModeON(imgAnnClass: HicomImageAnnotation) {
            if (imgAnnClass.InTouchMode == false) {
                imgAnnClass.InTouchMode = true;

                imgAnnClass.AnnotationCanvasCtrl.removeEventListener("dblclick", imgAnnClass.DblClickDrawingFunc);

                let arrowBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Arrow_${imgAnnClass.CtrlOutterId}`);
                let freehandBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Freehand_${imgAnnClass.CtrlOutterId}`);
                let circleBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Circle_${imgAnnClass.CtrlOutterId}`);
                let polyBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Poly_${imgAnnClass.CtrlOutterId}`);

                arrowBtn.style.display = "none";
                freehandBtn.style.display = "none";
                circleBtn.style.display = "none";

                polyBtn.lastChild.textContent = "Poly";
            }
        }

        /**
         * Handle when the image annotation tool comes out of touch mode.
         * @param imgAnnClass
         */
        private touchModeOFF(imgAnnClass: HicomImageAnnotation) {
            if (imgAnnClass.InTouchMode) {
                imgAnnClass.InTouchMode = false;

                imgAnnClass.AnnotationCanvasCtrl.removeEventListener("dblclick", imgAnnClass.DblClickDrawingFunc);

                let arrowBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Arrow_${imgAnnClass.CtrlOutterId}`);
                let freehandBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Freehand_${imgAnnClass.CtrlOutterId}`);
                let circleBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Circle_${imgAnnClass.CtrlOutterId}`);
                let polyBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Poly_${imgAnnClass.CtrlOutterId}`);

                arrowBtn.style.display = "";
                freehandBtn.style.display = "";
                circleBtn.style.display = "";

                polyBtn.lastChild.textContent = "Poly (double click to close)";
            }
        }

        /**
         * Adds a single point annotation.
         * @param event - The pointer event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private addSinglePoint(event: PointerEvent, imgAnnClass: HicomImageAnnotation) {
            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosPointerEvent(imgAnnClass.AnnotationCanvasCtrl, event);

            imgAnnClass.drawSinglePoint(imgAnnClass, posInfo);

            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            let annotationDialogue = imgAnnClass.setupKendoAnnotationDialog(function (e) {

                imgAnnClass.AnnotationCanvasCtrl.removeEventListener("click", imgAnnClass.ClickDrawingFunc);

                imgAnnClass.annotationOkayFunction(imgAnnClass, AnnotationMarkerTypeEnum.Point, e);

                imgAnnClass.drawAnnotationMarker(imgAnnClass, imgAnnClass.AllAnnotations.Annotations[imgAnnClass.AllAnnotations.Annotations.length - 1], imgAnnClass.CurrentPathPoints[0]);

                imgAnnClass.resetImgAnnClass(imgAnnClass);

                imgAnnClass.AnnotationCanvasCtrl.addEventListener("click", imgAnnClass.ClickDrawingFunc);
            }, function (e) {
                imgAnnClass.resetImgAnnClass(imgAnnClass);
                imgAnnClass.redrawAll(imgAnnClass);
                e.sender.close();
                e.sender.destroy();
            }, imgAnnClass);

            $(annotationDialogue).data("kendoDialog").open();
            imgAnnClass.setMarkerHandlers(imgAnnClass);
        }

        /**
         * Adds multiple joined points for annotation.
         * @param event - The pointer event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private addMultipleJoinedPoints(
            event: PointerEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosPointerEvent(imgAnnClass.AnnotationCanvasCtrl, event);

            this.drawSinglePoint(imgAnnClass, posInfo, false, true);

            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            if (imgAnnClass.CurrentPathPoints.length > 1) {

                //Draw a path between this one and the one before it.
                imgAnnClass.drawLine(imgAnnClass, imgAnnClass.CurrentPathPoints[imgAnnClass.CurrentPathPoints.length - 2], imgAnnClass.CurrentPathPoints[imgAnnClass.CurrentPathPoints.length - 1], false, true);
            }

            if (imgAnnClass.InTouchMode) {
                let polyBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Poly_${imgAnnClass.CtrlOutterId}`);

                if (imgAnnClass.CurrentPathPoints.length < 3) {
                    polyBtn.lastChild.textContent = "Remove current points (minimum three required)";
                } else {
                    polyBtn.lastChild.textContent = "Complete poly";
                }

                if (imgAnnClass.InPolyDrawing == false) {
                    imgAnnClass.InPolyDrawing = true;

                    polyBtn.removeEventListener("click", imgAnnClass.PolyButtonClickFunc);

                    let inPolyDrawingFunc = function (e: MouseEvent) {
                        if (imgAnnClass.CurrentPathPoints.length < 3) {
                            imgAnnClass.resetImgAnnClass(imgAnnClass, true);

                            polyBtn.addEventListener("click", imgAnnClass.PolyButtonClickFunc);
                            polyBtn.removeEventListener("click", inPolyDrawingFunc);
                        }
                        else {
                            imgAnnClass.closeMultiPointPath(e, imgAnnClass);

                            polyBtn.addEventListener("click", imgAnnClass.PolyButtonClickFunc);
                            polyBtn.removeEventListener("click", inPolyDrawingFunc);
                        }
                    }

                    polyBtn.addEventListener("click", inPolyDrawingFunc);
                }
            }
        }

        /**
         * Closes the multi-point path for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private closeMultiPointPath(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            if (imgAnnClass.CurrentPathPoints.length >= 3) {

                if (imgAnnClass.InTouchMode == false) {
                    //Remove the last posistion from the path array as this will always be recorded by the single click just before the double.
                    imgAnnClass.CurrentPathPoints.pop();
                }
                else {
                    imgAnnClass.AnnotationCanvasCtrl.removeEventListener("click", imgAnnClass.ClickDrawingFunc);
                }

                //Draw a line between the last point and the first point in the current path array.
                imgAnnClass.drawLine(imgAnnClass, imgAnnClass.CurrentPathPoints[0], imgAnnClass.CurrentPathPoints[imgAnnClass.CurrentPathPoints.length - 1]);

                // last point is the origin.
                imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(imgAnnClass.CurrentPathPoints[0].X, imgAnnClass.CurrentPathPoints[0].Y));

                let annotationDialogue = imgAnnClass.setupKendoAnnotationDialog(function (e) {

                    imgAnnClass.annotationOkayFunction(imgAnnClass, AnnotationMarkerTypeEnum.Poly, e);

                    imgAnnClass.drawAnnotationMarker(imgAnnClass, imgAnnClass.AllAnnotations.Annotations[imgAnnClass.AllAnnotations.Annotations.length - 1], imgAnnClass.CurrentPathPoints[0]);

                    imgAnnClass.resetImgAnnClass(imgAnnClass, true);

                    imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);

                    if (imgAnnClass.InTouchMode) {
                        imgAnnClass.AnnotationCanvasCtrl.addEventListener("click", imgAnnClass.ClickDrawingFunc);

                        let polyBtn: HTMLButtonElement = <HTMLButtonElement>document.getElementById(`btnImageAnno_Poly_${imgAnnClass.CtrlOutterId}`);

                        polyBtn.lastChild.textContent = "Poly";

                        imgAnnClass.InPolyDrawing = false;
                    }
                }, function (e) {
                    imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);
                    imgAnnClass.resetImgAnnClass(imgAnnClass);
                    imgAnnClass.redrawAll(imgAnnClass);
                    e.sender.close();
                    e.sender.destroy();
                }, imgAnnClass);

                $(annotationDialogue).data("kendoDialog").open();
                imgAnnClass.setMarkerHandlers(imgAnnClass);
            }
        }

        /**
         * Starts freehand drawing for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private startFreeHandJoinedPoints(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);

            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            imgAnnClass.IsDrawing = true;
        }

        /**
         * Moves freehand drawing for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private moveFreeHandJoinedPoints(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);

            let lengthOfPoints: number = imgAnnClass.CurrentPathPoints.length - 1;

            imgAnnClass.drawLine(imgAnnClass, posInfo, imgAnnClass.CurrentPathPoints[lengthOfPoints]);

            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));
        }

        /**
         * Ends freehand drawing for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private endFreeHandJoinedPoints(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            if (imgAnnClass.CurrentPathPoints.length >= 3) {

                imgAnnClass.drawLine(imgAnnClass, imgAnnClass.CurrentPathPoints[0], imgAnnClass.CurrentPathPoints[imgAnnClass.CurrentPathPoints.length - 1]);

                let annotationDialogue = imgAnnClass.setupKendoAnnotationDialog(function (e) {
                    imgAnnClass.annotationOkayFunction(imgAnnClass, AnnotationMarkerTypeEnum.Freehand, e);

                    //Annotation marker is drawn in the redraw method.

                    imgAnnClass.resetImgAnnClass(imgAnnClass, true);
                }, function (e) {
                    imgAnnClass.resetImgAnnClass(imgAnnClass);
                    imgAnnClass.redrawAll(imgAnnClass);
                    e.sender.close();
                    e.sender.destroy();
                }, imgAnnClass);

                $(annotationDialogue).data("kendoDialog").open();
                imgAnnClass.setMarkerHandlers(imgAnnClass);
            }
        }

        /**
         * Starts drawing an arrow for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private startArrowPoint(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);

            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            imgAnnClass.IsDrawing = true;
        }

        /**
         * Moves the arrow drawing for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private moveArrowJoinedPoints(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            if (imgAnnClass.IsDrawing) {
                let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);

                imgAnnClass.InflightPathPoint = new DataModel.PositionDM(posInfo.X, posInfo.Y);

                if (imgAnnClass.CurrentPathPoints[0]) {
                    imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);
                    imgAnnClass.drawArrow(imgAnnClass, imgAnnClass.CurrentPathPoints[0], imgAnnClass.InflightPathPoint, false, true);
                }

                imgAnnClass.PreviousPathPoint = new DataModel.PositionDM(posInfo.X, posInfo.Y);
            }
        }

        /**
         * Ends drawing an arrow for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private endArrowPoint(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);
            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);
            imgAnnClass.drawArrow(imgAnnClass, imgAnnClass.CurrentPathPoints[0], imgAnnClass.CurrentPathPoints[imgAnnClass.CurrentPathPoints.length - 1]);

            let annotationDialogue = imgAnnClass.setupKendoAnnotationDialog(function (e) {
                imgAnnClass.annotationOkayFunction(imgAnnClass, AnnotationMarkerTypeEnum.Arrow, e);

                //Annotation marker is drawn in the redraw method.

                imgAnnClass.resetImgAnnClass(imgAnnClass, true);
            }, function (e) {
                imgAnnClass.resetImgAnnClass(imgAnnClass);
                imgAnnClass.redrawAll(imgAnnClass);
                e.sender.close();
                e.sender.destroy();
            }, imgAnnClass);

            $(annotationDialogue).data("kendoDialog").open();
            imgAnnClass.setMarkerHandlers(imgAnnClass);
        }

        /**
         * Starts drawing a circle for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private startCirclePoint(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);

            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            imgAnnClass.IsDrawing = true;
        }

        /**
         * Moves the circle drawing for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private moveCircleJoinedPoints(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            if (imgAnnClass.IsDrawing) {
                let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);

                imgAnnClass.InflightPathPoint = new DataModel.PositionDM(posInfo.X, posInfo.Y);

                if (imgAnnClass.CurrentPathPoints[0]) {
                    imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);
                    imgAnnClass.drawCircle(imgAnnClass, imgAnnClass.CurrentPathPoints[0], imgAnnClass.InflightPathPoint, false, true);
                }

                imgAnnClass.PreviousPathPoint = new DataModel.PositionDM(posInfo.X, posInfo.Y);
            }
        }

        /**
         * Ends drawing a circle for annotation.
         * @param event - The mouse event.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private endCirclePoint(
            event: MouseEvent,
            imgAnnClass: HicomImageAnnotation) {

            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, event);
            imgAnnClass.CurrentPathPoints.push(new DataModel.PositionDM(posInfo.X, posInfo.Y));

            imgAnnClass.clearTempAnnotaionCanvas(imgAnnClass);

            imgAnnClass.drawCircle(imgAnnClass, imgAnnClass.CurrentPathPoints[0], imgAnnClass.CurrentPathPoints[imgAnnClass.CurrentPathPoints.length - 1]);

            let annotationDialogue = imgAnnClass.setupKendoAnnotationDialog(function (e) {
                imgAnnClass.annotationOkayFunction(imgAnnClass, AnnotationMarkerTypeEnum.Circle, e);

                //Annotation marker is drawn in the redraw method.

                imgAnnClass.resetImgAnnClass(imgAnnClass, true);
            }, function (e) {
                imgAnnClass.resetImgAnnClass(imgAnnClass);
                imgAnnClass.redrawAll(imgAnnClass);
                e.sender.close();
                e.sender.destroy();
            }, imgAnnClass);

            $(annotationDialogue).data("kendoDialog").open();
            imgAnnClass.setMarkerHandlers(imgAnnClass);
        }

        /**
         * Checks for annotation marker hit.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param e - The mouse event.
         */
        private checkForAnnotationMarkerHit(imgAnnClass: HicomImageAnnotation, e: MouseEvent) {
            //Highlights the associated annotation AND show the short description under the cursor.
            let posInfo: DataModel.PositionDM = imgAnnClass.getMousePosMouseEvent(imgAnnClass.AnnotationCanvasCtrl, e);

            let anyFound: boolean = false;

            for (var i = 0; i < imgAnnClass.AllAnnotations.Annotations.length; i++) {
                let annoToCheck: DataModel.AnnotationDM = imgAnnClass.AllAnnotations.Annotations[i];

                if (annoToCheck.MarkerLocation) {
                    if ((posInfo.X > annoToCheck.MarkerLocation.TopLeft.X && posInfo.X < annoToCheck.MarkerLocation.BottomRight.X) && (posInfo.Y > annoToCheck.MarkerLocation.TopLeft.Y && posInfo.Y < annoToCheck.MarkerLocation.BottomRight.Y)) {

                        imgAnnClass.ToolTipCtrl.style.top = `${posInfo.Y}px`;
                        imgAnnClass.ToolTipCtrl.style.left = `${posInfo.X}px`;

                        imgAnnClass.ToolTipCtrl.setAttribute("data-annotation", annoToCheck.AnnotationShortDesc);

                        if (imgAnnClass.CurrentToolTipText.length == 0) {

                            if (annoToCheck.AnnotationShortDesc.length > 0) {
                                $(imgAnnClass.ToolTipCtrl).show();

                                imgAnnClass.CurrentToolTipText = annoToCheck.AnnotationShortDesc;
                            }
                            else {
                                //We need to set it too something so that it does not keep getting updated.
                                imgAnnClass.CurrentToolTipText = `${annoToCheck.AnnotationNumber}`;
                            }

                            imgAnnClass.drawAddHighlighting(annoToCheck, imgAnnClass);
                            imgAnnClass.HighlightingToClear = true;

                            //Update the list item.
                            this.setAnnotationLiActive(imgAnnClass, annoToCheck);
                        }
                        else if (imgAnnClass.CurrentToolTipText == annoToCheck.AnnotationShortDesc || (annoToCheck.AnnotationShortDesc.length == 0 && imgAnnClass.CurrentToolTipText == `${annoToCheck.AnnotationNumber}`)) {

                            if (annoToCheck.AnnotationShortDesc.length > 0) {
                                $(imgAnnClass.ToolTipCtrl).show();
                            }

                            if (imgAnnClass.HighlightingToClear == false) {
                                imgAnnClass.HighlightingToClear = true;

                                imgAnnClass.drawAddHighlighting(annoToCheck, imgAnnClass);

                                //Update the list item.
                                this.setAnnotationLiActive(imgAnnClass, annoToCheck);
                            }
                        }
                        else if (imgAnnClass.CurrentToolTipText != annoToCheck.AnnotationShortDesc || (annoToCheck.AnnotationShortDesc.length == 0 && imgAnnClass.CurrentToolTipText != `${annoToCheck.AnnotationNumber}`)) {

                            if (annoToCheck.AnnotationShortDesc.length > 0) {
                                $(imgAnnClass.ToolTipCtrl).show();

                                imgAnnClass.CurrentToolTipText = annoToCheck.AnnotationShortDesc;
                            }
                            else {
                                //We need to set it too something so that it does not keep getting updated.
                                imgAnnClass.CurrentToolTipText = `${annoToCheck.AnnotationNumber}`;
                            }

                            imgAnnClass.HighlightingToClear = true;

                            imgAnnClass.drawAddHighlighting(annoToCheck, imgAnnClass);

                            //Update the list item.
                            this.setAnnotationLiActive(imgAnnClass, annoToCheck);
                        }

                        anyFound = true;
                    }
                }
            }

            if (anyFound == false) {
                $(imgAnnClass.ToolTipCtrl).hide();

                if (imgAnnClass.HighlightingToClear) {
                    imgAnnClass.redrawAll(imgAnnClass);
                    imgAnnClass.HighlightingToClear = false;
                    imgAnnClass.removeActiveFromAnnotationList(imgAnnClass);
                }
            }
        }

        //#region helpers

        //#region Drawing helpers

        /**
         * Draws a single point on the annotation canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param posInfo - The position information of the point.
         * @param highlight - Indicates if the point should be highlighted.
         * @param useTempContext - Indicates whether to use the temporary annotation context.
         */
        private drawSinglePoint(imgAnnClass: HicomImageAnnotation, posInfo: DataModel.PositionDM, highlight: boolean = false, useTempContext: boolean = false) {
            const context = useTempContext ? imgAnnClass.TempAnnotationContext : imgAnnClass.AnnotationContext;

            context.beginPath();

            if (highlight) {
                context.fillStyle = imgAnnClass.MarkerHighlightFillColour;
                context.strokeStyle = imgAnnClass.MarkerHighlightLineColour;
            }
            else {
                context.fillStyle = imgAnnClass.MarkerFillColour;
                context.strokeStyle = imgAnnClass.MarkerLineColour;
            }

            context.lineWidth = imgAnnClass.MarkerLineThickness;
            context.arc(posInfo.X, posInfo.Y, 5, 0, 2 * Math.PI);
            context.fill();
            context.stroke();
        }

        /**
         * Draws a line on the annotation canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param startPoint - The starting point of the line.
         * @param endPoint - The ending point of the line.
         * @param highlight - Indicates if the line should be highlighted.
         * @param useTempContext - Indicates whether to use the temporary annotation context.
         */
        private drawLine(imgAnnClass: HicomImageAnnotation, startPoint: DataModel.PositionDM, endPoint: DataModel.PositionDM, highlight: boolean = false, useTempContext: boolean = false) {
            const context = useTempContext ? imgAnnClass.TempAnnotationContext : imgAnnClass.AnnotationContext;

            context.beginPath();
            context.moveTo(startPoint.X, startPoint.Y);
            context.lineTo(endPoint.X, endPoint.Y);
            context.lineWidth = imgAnnClass.MarkerLineThickness;

            if (highlight) {
                context.strokeStyle = imgAnnClass.MarkerHighlightLineColour;
            }
            else {
                context.strokeStyle = imgAnnClass.MarkerLineColour;
            }

            context.stroke();
        }

        /**
         * Draws a circle on the annotation canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param firstPoint - The first point of the circle.
         * @param lastPoint - The last point of the circle.
         * @param highlight - Indicates if the circle should be highlighted.
         * @param useTempContext - Indicates whether to use the temporary annotation context.
         */
        private drawCircle(imgAnnClass: HicomImageAnnotation, firstPoint: DataModel.PositionDM, lastPoint: DataModel.PositionDM, highlight: boolean = false, useTempContext: boolean = false) {
            const context = useTempContext ? imgAnnClass.TempAnnotationContext : imgAnnClass.AnnotationContext;
            let radius: number = imgAnnClass.lineDistance(firstPoint, lastPoint);

            context.beginPath();
            context.arc(firstPoint.X, firstPoint.Y, radius, 0, 2 * Math.PI, false);
            context.lineWidth = imgAnnClass.MarkerLineThickness;

            if (highlight) {
                context.fillStyle = imgAnnClass.MarkerHighlightFillColour;
                context.strokeStyle = imgAnnClass.MarkerHighlightLineColour;
                context.fill();
            }
            else {
                context.fillStyle = imgAnnClass.MarkerFillColour;
                context.strokeStyle = imgAnnClass.MarkerLineColour;
            }

            context.stroke();
        }

        /**
         * Draws an arrow on the annotation canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param firstPoint - The starting point of the arrow.
         * @param lastPoint - The ending point of the arrow.
         * @param highlight - Indicates if the arrow should be highlighted.
         * @param useTempContext - Indicates whether to use the temporary annotation context.
         */
        private drawArrow(imgAnnClass: HicomImageAnnotation, firstPoint: DataModel.PositionDM, lastPoint: DataModel.PositionDM, highlight: boolean = false, useTempContext: boolean = false) {
            const context = useTempContext ? imgAnnClass.TempAnnotationContext : imgAnnClass.AnnotationContext;

            context.beginPath();
            context.moveTo(firstPoint.X, firstPoint.Y);
            context.lineTo(lastPoint.X, lastPoint.Y);
            context.lineWidth = imgAnnClass.MarkerLineThickness;

            if (highlight) {
                context.strokeStyle = imgAnnClass.MarkerHighlightLineColour;
            }
            else {
                context.strokeStyle = imgAnnClass.MarkerLineColour;
            }

            let startSize: number = 10;
            let endSize: number = 10;

            if (endSize > 0) {
                let lineAngle: number = Math.atan2(firstPoint.Y - lastPoint.Y, firstPoint.X - lastPoint.X);
                let delta: number = Math.PI / 6;
                for (var i = 0; i < 2; i++) {
                    context.moveTo(lastPoint.X, lastPoint.Y);
                    let x = lastPoint.X + endSize * Math.cos(lineAngle + delta);
                    let y = lastPoint.Y + endSize * Math.sin(lineAngle + delta);
                    context.lineTo(x, y);
                    context.lineWidth = imgAnnClass.MarkerLineThickness;
                    context.strokeStyle = imgAnnClass.MarkerLineColour;

                    if (highlight) {
                        context.strokeStyle = imgAnnClass.MarkerHighlightLineColour;
                    }
                    else {
                        context.strokeStyle = imgAnnClass.MarkerLineColour;
                    }

                    delta *= -1;
                }
            }
            context.stroke();
            context.closePath();
        }

        /**
         * Draws an annotation marker on the annotation canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param annotationToAdd - The annotation to add.
         * @param annotationMarkPoint - The position of the annotation marker.
         */
        private drawAnnotationMarker(imgAnnClass: HicomImageAnnotation, annotationToAdd: DataModel.AnnotationDM, annotationMarkPoint: DataModel.PositionDM) {

            //Saves the AnnotationContext's styles and other bits, we will restore to this at the end of this method.
            imgAnnClass.AnnotationContext.save();
            let annotationNumLength: number = annotationToAdd.AnnotationNumber.toString().length;

            let workingX: number;
            let workingY: number;

            if (annotationToAdd.AnnotationMarkerType == AnnotationMarkerTypeEnum.Circle) {
                if (annotationNumLength == 1) {
                    workingX = annotationMarkPoint.X - 10;
                } else if (annotationNumLength == 2) {
                    workingX = annotationMarkPoint.X - 15;
                } else if (annotationNumLength == 3) {
                    workingX = annotationMarkPoint.X - 20;
                } else if (annotationNumLength == 4) {
                    workingX = annotationMarkPoint.X - 25;
                }

                workingY = annotationMarkPoint.Y - 10;
            } else {
                workingX = annotationMarkPoint.X + 10;
                workingY = annotationMarkPoint.Y - 25;
            }

            let atTop: boolean = false;

            //Handle if the annotation marker is off the top.
            if (workingY <= 0) {
                workingY = 2;
                atTop = true;
            }

            imgAnnClass.AnnotationContext.beginPath();
            let widthSetTo: number = 0;

            if (annotationNumLength == 1) {

                if (workingX > (imgAnnClass.AnnotationCanvasCtrl.width - 22)) {
                    if (atTop) {
                        //Handle if the annotation marker is off both the top and right
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 32;
                        workingY = workingY + 10;
                    }
                    else {

                        //Handle if the annotation marker is off the right side.
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 22;
                    }
                }

                widthSetTo = 20;
                imgAnnClass.AnnotationContext.roundRect(workingX, workingY, widthSetTo, 20, [5]);

            } else if (annotationNumLength == 2) {

                if (workingX > (imgAnnClass.AnnotationCanvasCtrl.width - 32)) {
                    if (atTop) {
                        //Handle if the annotation marker is off both the top and right
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 42;
                        workingY = workingY + 10;
                    }
                    else {

                        //Handle if the annotation marker is off the right side.
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 32;
                    }
                }

                widthSetTo = 30;
                imgAnnClass.AnnotationContext.roundRect(workingX, workingY, widthSetTo, 20, [5]);
            } else if (annotationNumLength == 3) {


                if (workingX > (imgAnnClass.AnnotationCanvasCtrl.width - 42)) {
                    if (atTop) {
                        //Handle if the annotation marker is off both the top and right
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 52;
                        workingY = workingY + 10;
                    }
                    else {

                        //Handle if the annotation marker is off the right side.
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 42;
                    }
                }

                widthSetTo = 40;
                imgAnnClass.AnnotationContext.roundRect(workingX, workingY, widthSetTo, 20, [5]);
            } else if (annotationNumLength == 4) {

                if (workingX > (imgAnnClass.AnnotationCanvasCtrl.width - 52)) {
                    if (atTop) {
                        //Handle if the annotation marker is off both the top and right
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 62;
                        workingY = workingY + 10;
                    }
                    else {

                        //Handle if the annotation marker is off the right side.
                        workingX = imgAnnClass.AnnotationCanvasCtrl.width - 52;
                    }
                }

                widthSetTo = 50;
                imgAnnClass.AnnotationContext.roundRect(workingX, workingY, widthSetTo, 20, [5]);
            }

            imgAnnClass.AnnotationContext.stroke();

            imgAnnClass.AnnotationContext.fillStyle = imgAnnClass.AnnotationMarkerColour;
            imgAnnClass.AnnotationContext.fill();

            // Add the fill text.
            imgAnnClass.AnnotationContext.font = "bold 16px Rubik, sans-serif";
            imgAnnClass.AnnotationContext.fillStyle = imgAnnClass.AnnotationTextColour;

            imgAnnClass.AnnotationContext.fillText(annotationToAdd.AnnotationNumber.toString(), workingX + 6, workingY + 15);

            //Restore back to previous styles
            imgAnnClass.AnnotationContext.restore();

            //Add the marker position to the annotation.
            annotationToAdd.MarkerLocation = new DataModel.RectPositionDM(new DataModel.PositionDM(workingX, workingY), new DataModel.PositionDM(workingX + widthSetTo, workingY + 20));
        }

        /**
         * Draws and highlights an annotation on the annotation canvas.
         * @param annotation - The annotation to highlight.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private drawAddHighlighting(annotation: DataModel.AnnotationDM, imgAnnClass: HicomImageAnnotation) {

            if (annotation.AnnotationMarkerType == AnnotationMarkerTypeEnum.Point) {
                imgAnnClass.drawSinglePoint(imgAnnClass, annotation.MarkedPositions[0], true);
            } else if (annotation.AnnotationMarkerType == AnnotationMarkerTypeEnum.Arrow) {
                imgAnnClass.drawArrow(imgAnnClass, annotation.MarkedPositions[0], annotation.MarkedPositions[annotation.MarkedPositions.length - 1], true);
            } else if (annotation.AnnotationMarkerType == AnnotationMarkerTypeEnum.Poly) {
                let polyPath: Path2D = new Path2D();
                polyPath.moveTo(annotation.MarkedPositions[0].X, annotation.MarkedPositions[0].Y);

                for (var i = 0; i < annotation.MarkedPositions.length; i++) {
                    polyPath.lineTo(annotation.MarkedPositions[i].X, annotation.MarkedPositions[i].Y);
                }

                imgAnnClass.AnnotationContext.fillStyle = imgAnnClass.MarkerHighlightFillColour;
                imgAnnClass.AnnotationContext.fill(polyPath);

            } else if (annotation.AnnotationMarkerType == AnnotationMarkerTypeEnum.Freehand) {
                let fhPath: Path2D = new Path2D();
                fhPath.moveTo(annotation.MarkedPositions[0].X, annotation.MarkedPositions[0].Y);

                for (var i = 0; i < annotation.MarkedPositions.length; i++) {
                    fhPath.lineTo(annotation.MarkedPositions[i].X, annotation.MarkedPositions[i].Y);
                }

                imgAnnClass.AnnotationContext.fillStyle = imgAnnClass.MarkerHighlightFillColour;
                imgAnnClass.AnnotationContext.fill(fhPath);

            } else if (annotation.AnnotationMarkerType == AnnotationMarkerTypeEnum.Circle) {
                imgAnnClass.drawCircle(imgAnnClass, annotation.MarkedPositions[0], annotation.MarkedPositions[annotation.MarkedPositions.length - 1], true);

            } else {
                throw new DOMException("Missing AnnotationMarkerType");
            }
        }

        /**
         * This function is to clear down all the drawings on the temporary canvas
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private clearTempAnnotaionCanvas(imgAnnClass: HicomImageAnnotation) {
            imgAnnClass.TempAnnotationContext.clearRect(0, 0, imgAnnClass.TempAnnotationCanvasCtrl.width, imgAnnClass.TempAnnotationCanvasCtrl.height);
        }

        //#endregion

        /**
         * Gets the mouse position from a PointerEvent.
         * @param canvas - The canvas element.
         * @param evt - The PointerEvent.
         * @returns The mouse position as a DataModel.PositionDM object.
         */
        private getMousePosPointerEvent(canvas: HTMLCanvasElement, evt: PointerEvent): DataModel.PositionDM {
            var rect = canvas.getBoundingClientRect(), // abs. size of element
                scaleX = canvas.width / rect.width,    // relationship bitmap vs. element for x
                scaleY = canvas.height / rect.height;  // relationship bitmap vs. element for y

            // X = scale mouse coordinates after they have
            // Y = been adjusted to be relative to element
            return new DataModel.PositionDM((evt.clientX - rect.left) * scaleX, (evt.clientY - rect.top) * scaleY);
        }

        /**
         * Gets the mouse position from a MouseEvent.
         * @param canvas - The canvas element.
         * @param evt - The MouseEvent.
         * @returns The mouse position as a DataModel.PositionDM object.
         */
        private getMousePosMouseEvent(canvas: HTMLCanvasElement, evt: MouseEvent): DataModel.PositionDM {
            var rect = canvas.getBoundingClientRect(), // abs. size of element
                scaleX = canvas.width / rect.width,    // relationship bitmap vs. element for x
                scaleY = canvas.height / rect.height;  // relationship bitmap vs. element for y

            // X = scale mouse coordinates after they have
            // Y = been adjusted to be relative to element
            return new DataModel.PositionDM((evt.clientX - rect.left) * scaleX, (evt.clientY - rect.top) * scaleY);
        }

        /**
         * Calculates the distance between two points.
         * @param startPoint - The starting point.
         * @param endPoint - The ending point.
         * @returns The distance between the points.
         */
        private lineDistance(startPoint: DataModel.PositionDM, endPoint: DataModel.PositionDM): number {
            let xs: number = 0;
            let ys: number = 0;

            xs = endPoint.X - startPoint.X;
            xs = xs * xs;

            ys = endPoint.Y - startPoint.Y;
            ys = ys * ys;

            return Math.sqrt(xs + ys);
        }

        /**
         * Redraws all annotations on the canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private redrawAll(imgAnnClass: HicomImageAnnotation) {
            // This will make sure in case of theme change the colours are updated.
            this.setActiveThemeColoursFromCssClasses();

            // "clear" the entire thing (actually set it to transparent black), remember the canvas is just a load of pixels, rather than controls.
            imgAnnClass.AnnotationContext.clearRect(0, 0, imgAnnClass.AnnotationCanvasCtrl.width, imgAnnClass.AnnotationCanvasCtrl.height);

            let img = new Image();
            img.onload = function () {

                //Redraw points.
                for (let item of imgAnnClass.AllAnnotations.Annotations) {
                    if (item.AnnotationMarkerType === AnnotationMarkerTypeEnum.Point) {
                        imgAnnClass.drawSinglePoint(imgAnnClass, item.MarkedPositions[0]);

                        imgAnnClass.drawAnnotationMarker(imgAnnClass, item, item.MarkedPositions[0]);
                    }
                }

                //Redraw arrows
                for (let item of imgAnnClass.AllAnnotations.Annotations) {
                    if (item.AnnotationMarkerType === AnnotationMarkerTypeEnum.Arrow) {
                        imgAnnClass.drawArrow(imgAnnClass, item.MarkedPositions[0], item.MarkedPositions[item.MarkedPositions.length - 1]);

                        imgAnnClass.drawAnnotationMarker(imgAnnClass, item, item.MarkedPositions[item.MarkedPositions.length - 1]);
                    }
                }

                //Redraw Poly
                for (let item of imgAnnClass.AllAnnotations.Annotations) {
                    if (item.AnnotationMarkerType === AnnotationMarkerTypeEnum.Poly) {

                        let firstOccured: boolean = false;

                        for (var i = 0; i < item.MarkedPositions.length; i++) {
                            imgAnnClass.drawSinglePoint(imgAnnClass, item.MarkedPositions[i]);

                            if (firstOccured) {
                                imgAnnClass.drawLine(imgAnnClass, item.MarkedPositions[i - 1], item.MarkedPositions[i]);
                            }

                            firstOccured = true;
                        }

                        imgAnnClass.drawAnnotationMarker(imgAnnClass, item, item.MarkedPositions[0]);
                    }
                }

                //Redraw Freehand
                for (let item of imgAnnClass.AllAnnotations.Annotations) {
                    if (item.AnnotationMarkerType === AnnotationMarkerTypeEnum.Freehand) {

                        let firstOccured: boolean = false;

                        for (var i = 0; i < item.MarkedPositions.length; i++) {
                            if (firstOccured) {
                                imgAnnClass.drawLine(imgAnnClass, item.MarkedPositions[i - 1], item.MarkedPositions[i]);
                            }

                            firstOccured = true;
                        }

                        imgAnnClass.drawLine(imgAnnClass, item.MarkedPositions[0], item.MarkedPositions[item.MarkedPositions.length - 1])

                        imgAnnClass.drawAnnotationMarker(imgAnnClass, item, item.MarkedPositions[0]);
                    }
                }

                //Redraw Circle
                for (let item of imgAnnClass.AllAnnotations.Annotations) {
                    if (item.AnnotationMarkerType === AnnotationMarkerTypeEnum.Circle) {
                        imgAnnClass.drawCircle(imgAnnClass, item.MarkedPositions[0], item.MarkedPositions[item.MarkedPositions.length - 1]);

                        imgAnnClass.drawAnnotationMarker(imgAnnClass, item, item.MarkedPositions[0]);
                    }
                }


                const event = new Event("redrawCompleted");

                //This event is used in the external method which generates HTML output
                imgAnnClass.AnnotationCanvasCtrl.dispatchEvent(event);

                imgAnnClass.AnnotationCanvasCtrl.setAttribute("data-inital-load-complete", "true");
            };

            img.src = imgAnnClass.ImgSrc;
        }

        private handleThemeChanges(themesDropdownId: string, imgAnnClass: HicomImageAnnotation) {
            const themeChangeDropDown = document.getElementById(themesDropdownId);
            if (themeChangeDropDown) {
                themeChangeDropDown.addEventListener('change', () => {
                    imgAnnClass.redrawAll(imgAnnClass);
                });
            }
        }

        /**
         * Resets the HicomImageAnnotation instance.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param redrawNeeded - Indicates if a redraw is needed.
         */
        private resetImgAnnClass(imgAnnClass: HicomImageAnnotation,
            redrawNeeded: boolean = false) {
            imgAnnClass.CurrentPathPoints = new Array<DataModel.PositionDM>();
            imgAnnClass.IsDrawing = false;
            imgAnnClass.InflightPathPoint = null;
            imgAnnClass.PreviousPathPoint = null;

            if (redrawNeeded) {
                imgAnnClass.redrawAll(imgAnnClass);
            }
        }

        /**
         * Sets up a Kendo UI dialog for annotations.
         * @param actionOK - The function to execute on OK.
         * @param actionCancel - The function to execute on Cancel.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param shortText - The short text for the dialog.
         * @param longText - The long text for the dialog.
         * @returns The Kendo UI dialog instance.
         */
        private setupKendoAnnotationDialog(actionOK: Function, actionCancel: Function, imgAnnClass: HicomImageAnnotation, shortText: string = "", longText: string = ""): any {
            var dialog = $(`<div id="divDialog_${imgAnnClass.CtrlOutterId}"></div>`).kendoDialog({
                width: "400px",
                visible: false,
                title: "Annotation",
                closable: true,
                modal: true,
                actions: [
                    { text: 'Cancel', action: actionCancel },
                    { text: 'OK', primary: true, action: actionOK }
                ]
            });

            if (imgAnnClass.AnnotationsDisplayMode == AnnotationsDisplayModeEnum.OutsideImageInListOnly) {
                //Single annotation only
                $(dialog).data("kendoDialog").content(`
            <div class="form-row">
            <textarea wrap="soft" cols="5" rows="3" id="txtAnnotationLongText_${imgAnnClass.CtrlOutterId}" data-hicom-role="hicom-input" type="text"  class="h-form-control hicom-input valid" placeholder="Please enter some text">${longText}</textarea>
            </div>`);
            }
            else if (imgAnnClass.AnnotationsDisplayMode == AnnotationsDisplayModeEnum.HoverShortTextAndInOutsideList) {
                //Two text boxes for short and long annotations.
                $(dialog).data("kendoDialog").content(`<div class="form-row">
            <label data-hicom-role="hicom-label" for="txtAnnotationShortText_${imgAnnClass.CtrlOutterId}" class="hicom-label hicom-required">Short annotation</label>
            <input id="txtAnnotationShortText_${imgAnnClass.CtrlOutterId}" data-hicom-role="hicom-input" type="text" value="${shortText}" class="h-form-control hicom-input valid" placeholder="Please enter some text"/>
            </div>
            <div class="form-row">
            <label data-hicom-role="hicom-label" for="txtAnnotationLongText_${imgAnnClass.CtrlOutterId}" class="hicom-label hicom-required">Full annotation</label>
            <textarea wrap="soft" cols="5" rows="3" id="txtAnnotationLongText_${imgAnnClass.CtrlOutterId}" data-hicom-role="hicom-input" type="text"  class="h-form-control hicom-input valid" placeholder="Please enter some text">${longText}</textarea>
            </div>`);
            }

            return dialog;
        }

        /**
         * Draws an annotation marker on the canvas.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param annotationToAdd - The annotation to add.
         * @param annotationMarkPoint - The position of the annotation marker.
         */
        private addAllAnnotationsToList(imgAnnClass: HicomImageAnnotation) {
            let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

            for (let item of imgAnnClass.AllAnnotations.Annotations) {
                olAnnoLst.appendChild(this.createAnnotationListItem(item, imgAnnClass));
            }
        }

        /**
         * Adds the last annotation to the list.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private addLastAnnotationToList(imgAnnClass: HicomImageAnnotation) {
            let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

            olAnnoLst.appendChild(this.createAnnotationListItem(imgAnnClass.AllAnnotations.Annotations[imgAnnClass.AllAnnotations.Annotations.length - 1], imgAnnClass));
        }

        /**
         * Creates an annotation list item.
         * @param annotation - The annotation data model.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @returns The created HTML list item element.
         */
        private createAnnotationListItem(annotation: DataModel.AnnotationDM, imgAnnClass: HicomImageAnnotation): HTMLLIElement {
            let li: HTMLLIElement = <HTMLLIElement>document.createElement("li");

            if (imgAnnClass.ReadOnlyMode == false) {
                let editSpanIcon: HTMLSpanElement = <HTMLSpanElement>document.createElement("a");
                editSpanIcon.className = "icon-edit edit icon-only";
                editSpanIcon.setAttribute("href", "#");

                let deleteSpanIcon: HTMLSpanElement = <HTMLSpanElement>document.createElement("a");
                deleteSpanIcon.className = "icon-delete delete icon-only";
                deleteSpanIcon.setAttribute("href", "#");

                li.appendChild(editSpanIcon);
                li.appendChild(deleteSpanIcon);
            }

            let annotationText: HTMLDivElement = <HTMLDivElement>document.createElement("div");
            annotationText.className = "annotation-text";
            if (annotation.AnnotationLongDesc.length == 0) {
                annotationText.innerHTML = annotationText.innerHTML;
            }
            else {
                annotationText.innerHTML = annotationText.innerHTML + `${annotation.AnnotationLongDesc}`;
            }

            li.appendChild(annotationText);

            let redrawNeeded: boolean = true;

            let curriedMouseDownTouchStartHighlight = function (e: UIEvent) {

                let li: HTMLLIElement = <HTMLLIElement>e.currentTarget;

                if ((<HTMLSpanElement>e.target).classList.contains("icon-edit")) {
                    let annotationDialogue = imgAnnClass.setupKendoAnnotationDialog(function (e) {

                        let { shortText, longText }: { shortText: string; longText: string; } = imgAnnClass.getShortAndLongTextFromDialog(imgAnnClass);

                        annotation.AnnotationShortDesc = shortText;
                        annotation.AnnotationLongDesc = longText;

                        let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

                        while (olAnnoLst.firstChild) {
                            olAnnoLst.firstChild.remove();
                        }

                        imgAnnClass.addAllAnnotationsToList(imgAnnClass);

                        //Reset the annotation numbers.
                        for (var i = 0; i < imgAnnClass.AllAnnotations.Annotations.length; i++) {
                            imgAnnClass.AllAnnotations.Annotations[i].AnnotationNumber = i + 1;
                            imgAnnClass.CurrentAnnotationNumber = i + 1;
                        }

                        imgAnnClass.updateHdnJsonAndSetDirty(imgAnnClass);
                        imgAnnClass.resetImgAnnClass(imgAnnClass, true);

                        li.classList.remove("active");

                        e.sender.close();
                        e.sender.destroy();
                    }, function (e) {
                        li.classList.remove("active");
                        e.sender.close();
                        e.sender.destroy();
                    }, imgAnnClass, annotation.AnnotationShortDesc, annotation.AnnotationLongDesc);

                    $(annotationDialogue).data("kendoDialog").open();
                    redrawNeeded = false;
                }
                else if ((<HTMLSpanElement>e.target).classList.contains("icon-delete")) {

                    var dialog = $(`<div id="divDialogRemove_${imgAnnClass.CtrlOutterId}"></div>`).kendoDialog({
                        width: "400px",
                        visible: false,
                        title: imgAnnClass.ResxRemoveAnnotationTitle,
                        closable: true,
                        modal: true,
                        content: imgAnnClass.ResxRemoveAnnotationContent,
                        actions: [
                            {
                                text: 'Cancel', action: function (e) {
                                    li.classList.remove("active");
                                    e.sender.close();
                                    e.sender.destroy();
                                }
                            }
                            , {
                                //@ts-ignore
                                text: 'Delete', cssClass: "btn btn-danger", primary: true, action: function (e) {
                                    imgAnnClass.AllAnnotations.Annotations.splice(imgAnnClass.AllAnnotations.Annotations.findIndex(item => item.AnnotationNumber === annotation.AnnotationNumber), 1)

                                    //Remove and re add all annotations from the list.
                                    let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

                                    while (olAnnoLst.firstChild) {
                                        olAnnoLst.firstChild.remove();
                                    }

                                    imgAnnClass.addAllAnnotationsToList(imgAnnClass);

                                    //Reset the annotation numbers.
                                    for (var i = 0; i < imgAnnClass.AllAnnotations.Annotations.length; i++) {
                                        imgAnnClass.AllAnnotations.Annotations[i].AnnotationNumber = i + 1;
                                        imgAnnClass.CurrentAnnotationNumber = i + 1;
                                    }

                                    imgAnnClass.updateHdnJsonAndSetDirty(imgAnnClass);
                                    imgAnnClass.resetImgAnnClass(imgAnnClass, true);

                                    li.classList.remove("active");
                                    e.sender.close();
                                    e.sender.destroy();
                                }
                            }
                        ]
                    });

                    $(dialog).data("kendoDialog").open();
                    redrawNeeded = false;
                }
                else {
                    imgAnnClass.drawAddHighlighting(annotation, imgAnnClass);
                    redrawNeeded = true;
                }

                li.classList.add("active");
            }

            let curriedMouseUpTouchEndHighlight = function (e: UIEvent) {
                if (redrawNeeded) {
                    imgAnnClass.redrawAll(imgAnnClass);
                }

                let li: HTMLLIElement = <HTMLLIElement>e.currentTarget;
                li.classList.remove("active");
            }

            if (imgAnnClass.InTouchMode) {
                li.removeEventListener("touchstart", curriedMouseDownTouchStartHighlight);
                li.removeEventListener("touchend", curriedMouseUpTouchEndHighlight);

                li.addEventListener("touchstart", curriedMouseDownTouchStartHighlight);
                li.addEventListener("touchend", curriedMouseUpTouchEndHighlight);
            }
            else {
                li.removeEventListener("mousedown", curriedMouseDownTouchStartHighlight);
                li.removeEventListener("mouseup", curriedMouseUpTouchEndHighlight);

                li.addEventListener("mousedown", curriedMouseDownTouchStartHighlight);
                li.addEventListener("mouseup", curriedMouseUpTouchEndHighlight);
            }

            return li;
        }

        /**
         * Sets the annotation list item as active.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param annoToCheck - The annotation data model to check.
         */
        private setAnnotationLiActive(imgAnnClass: HicomImageAnnotation, annoToCheck: DataModel.AnnotationDM) {
            let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

            for (let item of imgAnnClass.AllAnnotations.Annotations) {
                for (var i = 0; i < olAnnoLst.children.length; i++) {
                    if (annoToCheck.AnnotationNumber == (i + 1)) {
                        olAnnoLst.children[i].classList.add("active");
                    }
                }
            }
        }

        /**
         * Removes the active class from all annotation list items.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private removeActiveFromAnnotationList(imgAnnClass: HicomImageAnnotation) {
            let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

            for (var i = 0; i < olAnnoLst.children.length; i++) {
                olAnnoLst.children[i].classList.remove("active");
            }
        }

        /**
         * Removes all the annotations from the list
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private removeAllAnnotationsFromList(imgAnnClass: HicomImageAnnotation) {
            let olAnnoLst: HTMLOListElement = <HTMLOListElement>document.getElementById(`olAnnotationList_${imgAnnClass.CtrlOutterId}`);

            for (var i = 0; i < olAnnoLst.children.length; i++) {
                olAnnoLst.children[i].remove();
            }
        }

        /**
         * Handles the OK action for the annotation dialog.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @param markerType - The type of marker.
         * @param e - The event object.
         */
        private annotationOkayFunction(imgAnnClass: HicomImageAnnotation, markerType: AnnotationMarkerTypeEnum, e: any) {
            imgAnnClass.CurrentAnnotationNumber++;

            let { shortText, longText }: { shortText: string; longText: string; } = imgAnnClass.getShortAndLongTextFromDialog(imgAnnClass);

            imgAnnClass.AllAnnotations.Annotations.push(
                new DataModel.AnnotationDM(
                    imgAnnClass.CurrentAnnotationNumber,
                    shortText,
                    longText,
                    imgAnnClass.CurrentPathPoints,
                    markerType
                )
            );

            imgAnnClass.addLastAnnotationToList(imgAnnClass);

            imgAnnClass.updateHdnJsonAndSetDirty(imgAnnClass);

            e.sender.close();
            e.sender.destroy();
        }

        /**
         * Updates the hidden JSON field and sets the form as dirty.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         */
        private updateHdnJsonAndSetDirty(imgAnnClass: HicomImageAnnotation) {
            let hdnImgAnnoJsonHoler: HTMLInputElement = <HTMLInputElement>document.getElementById(`${imgAnnClass.HiddenAnnoJsonHolder}${imgAnnClass.CtrlOutterId}`);

            //Add the annotations to the hidden field, this is what is saved to the DB
            hdnImgAnnoJsonHoler.value = JSON.stringify(imgAnnClass.AllAnnotations);

            let closestForm = $(imgAnnClass.AnnotationCanvasCtrl).closest('form')[0];

            if (closestForm) {
                let hicomFormCtrl: HicomForm = new HicomForm(closestForm.id);

                hicomFormCtrl.SetDirty();

                HicomDirtyForms.setDirtyStatus($(hdnImgAnnoJsonHoler), hdnImgAnnoJsonHoler.value);
            }
        }

        /**
         * Retrieves the short and long text from the annotation dialog.
         * @param imgAnnClass - The instance of HicomImageAnnotation.
         * @returns An object containing the short and long text.
         */
        private getShortAndLongTextFromDialog(imgAnnClass: HicomImageAnnotation) {
            let shortText: string = "";
            let longText: string = "";

            if (imgAnnClass.AnnotationsDisplayMode == AnnotationsDisplayModeEnum.HoverShortTextAndInOutsideList) {
                let shortAnnoTextBox: HTMLInputElement = <HTMLInputElement>document.getElementById(`txtAnnotationShortText_${imgAnnClass.CtrlOutterId}`);
                shortText = shortAnnoTextBox.value;
            }

            let longAnnoTextBox: HTMLTextAreaElement = <HTMLTextAreaElement>document.getElementById(`txtAnnotationLongText_${imgAnnClass.CtrlOutterId}`);

            longText = longAnnoTextBox.value;
            return { shortText, longText };
        }

        //#endregion

        //#region External helpers

        /**
         * This method will return a printable HTML string for the passed in Canvas ID's.
         * @param mainDivID The main div ID of the image annotation control.
         * @param canvasImageId The canvas image ID of the image annotation control.
         * @param canvasAnnotationId The canvas annotation ID of the image annotation control.
         * @param callBackFunctionAfterOnLoadOfImage The callback function to execute after the image has loaded.
         */
        public static getPrintableHtmlForImgAnnotationCtrl(mainDivID: string, canvasImageId: string, canvasAnnotationId, callBackFunctionAfterOnLoadOfImage: Function) {
            let canvasAnnotation: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById(canvasAnnotationId);

            if (canvasAnnotation.dataset.initalLoadComplete && canvasAnnotation.dataset.initalLoadComplete.toLowerCase() === 'true') {
                HicomImageAnnotation.internalsForGettingPrintableHTML(mainDivID, canvasImageId, canvasAnnotationId, callBackFunctionAfterOnLoadOfImage);
            }
            else {
                let redrawCompletedFunc = function (e) {
                    HicomImageAnnotation.internalsForGettingPrintableHTML(mainDivID, canvasImageId, canvasAnnotationId, callBackFunctionAfterOnLoadOfImage);

                    canvasAnnotation.removeEventListener("redrawCompleted", redrawCompletedFunc);
                }

                canvasAnnotation.addEventListener("redrawCompleted", redrawCompletedFunc);
            }
        }

        /** A internal method for getting the HTML from the image annotation tool
        * @param mainDivID The main div ID of the image annotation control.
        * @param canvasImageId The canvas image ID of the image annotation control.
        * @param canvasAnnotationId The canvas annotation ID of the image annotation control.
        * @param callBackFunctionAfterOnLoadOfImage The callback function to execute after the image has loaded.
        */
        private static internalsForGettingPrintableHTML(mainDivID: string, canvasImageId: string, canvasAnnotationId, callBackFunctionAfterOnLoadOfImage: Function) {

            let mainDiv: HTMLDivElement = <HTMLDivElement>document.getElementById(mainDivID);
            let canvasImage: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById(canvasImageId);
            let canvasAnnotation: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById(canvasAnnotationId);

            let combineCanvas: HTMLCanvasElement = <HTMLCanvasElement>document.createElement("canvas");
            combineCanvas.id = "CombineCanvas";
            combineCanvas.height = canvasAnnotation.height;
            combineCanvas.width = canvasAnnotation.width;

            let ctx: CanvasRenderingContext2D = <CanvasRenderingContext2D>combineCanvas.getContext("2d");

            ctx.drawImage(canvasImage, 0, 0, canvasImage.width, canvasImage.height, // source rectangle
                0, 0, canvasImage.width, canvasImage.height); // destination rectangle

            ctx.drawImage(canvasAnnotation, 0, 0, canvasAnnotation.width, canvasAnnotation.height, // source rectangle
                0, 0, canvasAnnotation.width, canvasAnnotation.height); // destination rectangle

            let data: string = combineCanvas.toDataURL("image/png");

            let clonedDiv: HTMLDivElement = <HTMLDivElement>mainDiv.cloneNode(true);

            clonedDiv.querySelector("script").remove();
            clonedDiv.querySelector('input[type="hidden"]').remove();
            clonedDiv.querySelector('.image-annotation-tools').remove();

            let canvasImg: HTMLImageElement = new Image();
            canvasImg.src = data;

            //Parent of the canvas is the holder, parent of that is the col which is where we want to put the new image.
            let divCol: HTMLDivElement = <HTMLDivElement>clonedDiv.querySelector('canvas').parentElement.parentElement;

            divCol.removeChild(divCol.firstChild);

            divCol.appendChild(canvasImg);

            canvasImg.onload = function () {
                return callBackFunctionAfterOnLoadOfImage(clonedDiv.outerHTML);
            }
        }

        /**
         * The kendo control on a published form will call this method to load the image annotation control, this method works by getting the 
         * @param e The event passed in from the kendo control.         
         */
        public static KendoUploaderDeferredLoadImageAnnotationTool(e: any) {
            if (e.response) {
                let successDM: DataModel.ImageUserUploadSuccessDM = <DataModel.ImageUserUploadSuccessDM>e.response;

                HicomImageAnnotation.InitDeferredImgAnno(successDM.HiddenImgAnnoDetailsID, successDM.ImageBase64Src);

                let divUploaderCtrlHolder: HTMLDivElement = <HTMLDivElement>document.getElementById(`divUploaderCtrlHolder_${successDM.ImgAnnoCtrlID}`);

                divUploaderCtrlHolder.style.display = "none";

                let divImageAnnoCtrlHolder: HTMLDivElement = <HTMLDivElement>document.getElementById(`divImageAnnoCtrlHolder_${successDM.ImgAnnoCtrlID}`);

                divImageAnnoCtrlHolder.style.display = "block";

                let hdnRespInfoStreamId: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadStreamId_${successDM.FormFieldId}`);
                let hdnRespInfoUploadedDate: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadUploadedDate_${successDM.FormFieldId}`);
                let hdnRespInfoUploadedBy: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadUploadedBy_${successDM.FormFieldId}`);
                let hdnRespInfoFileName: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadFileName_${successDM.FormFieldId}`);
                let hdnRespInfoFileValueObjectId: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadFileValueObjectId_${successDM.FormFieldId}`);
                let hdnimageAnnotationUserFileUploadStatus: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadStatus_${successDM.FormFieldId}`);

                hdnRespInfoStreamId.value = successDM.StreamId;
                hdnRespInfoUploadedDate.value = new Date().toJSON();
                hdnRespInfoUploadedBy.value = successDM.UploadedBy.toString();
                hdnRespInfoFileName.value = successDM.FileName;
                hdnRespInfoFileValueObjectId.value = successDM.FileValueObjectId;
                hdnimageAnnotationUserFileUploadStatus.value = successDM.Status.toString();
            }
        }

        /**
         * Call this method when Deferred = true. This will load the information in the hidden field made that was made during the tag helper construction, and use it to initialize the image annotation tool.
         * @param HiddenImgAnnoDetailsID The ID of the hidden input tag which holds all the info for setting up a image annotation control.
         * @param ImageBase64Src The image that will be used.
         */
        public static InitDeferredImgAnno(HiddenImgAnnoDetailsID: string, ImageBase64Src: string) {
            let hiddenInput: HTMLInputElement = <HTMLInputElement>document.getElementById(HiddenImgAnnoDetailsID);

            if (!hiddenInput) {
                console.error(`Hidden input with ID ${HiddenImgAnnoDetailsID} not found.`);
                return;
            }

            // Deserialize the JSON data
            let deferredData = JSON.parse(hiddenInput.value);

            // Instantiate a new HicomImageAnnotation
            let imgAnno = new HicomImageAnnotation(
                deferredData.Id,
                deferredData.MarkerLineThickness,
                deferredData.MarkerLineColour,
                deferredData.MarkerFillColour,
                deferredData.MarkerHighlightLineColour,
                deferredData.MarkerHighlightFillColour,
                deferredData.AnnotationMarkerColour,
                deferredData.AnnotationTextColour,
                deferredData.AnnotationsDisplayMode,
                deferredData.ReadOnlyMode,
                deferredData.RemoveAnnotationTitle,
                deferredData.RemoveAnnotation,
                deferredData.AnnotationListLocation,
                deferredData.MarkerType,
                deferredData.ForceImageHeight,
                deferredData.ForceImageWidth,
                deferredData.CanvasImagePartialID,
                deferredData.CanvasAnnotationPartialID,
                deferredData.HiddenAnnoJsonHolder
            );

            // Call the InitImgAnnoCanvas method
            imgAnno.InitImgAnnoCanvas(ImageBase64Src);
        }

        /**
         * Call this method when Deferred = true. This will load the information in the hidden field made that was made during the tag helper construction, and use it to initialize the image annotation tool.
         * This method will get the image using the stream ID provided.
         * @param HiddenImgAnnoDetailsID The ID of the hidden input tag which holds all the info for setting up a image annotation control.
         * @param GetBase64ImageByStreamIDUrl The URL used to get the base 64 version of the image by stream ID.
         */
        public static InitDeferredImgAnnoStreamID(HiddenImgAnnoDetailsID: string, GetBase64ImageByStreamIDUrl: string) {
            let reqVerificationToken: string = $('input:hidden[name="__RequestVerificationToken"]').val().toString();

            fetch(GetBase64ImageByStreamIDUrl, {
                method: 'GET',
                headers: {
                    'RequestVerificationToken': reqVerificationToken
                }
            })
                .then(response => response.json())
                .then(data => {
                    let hiddenInput: HTMLInputElement = <HTMLInputElement>document.getElementById(HiddenImgAnnoDetailsID);

                    if (!hiddenInput) {
                        console.error(`Hidden input with ID ${HiddenImgAnnoDetailsID} not found.`);
                        return;
                    }

                    // Deserialize the JSON data
                    let deferredData = JSON.parse(hiddenInput.value);

                    // Instantiate a new HicomImageAnnotation
                    let imgAnno = new HicomImageAnnotation(
                        deferredData.Id,
                        deferredData.MarkerLineThickness,
                        deferredData.MarkerLineColour,
                        deferredData.MarkerFillColour,
                        deferredData.MarkerHighlightLineColour,
                        deferredData.MarkerHighlightFillColour,
                        deferredData.AnnotationMarkerColour,
                        deferredData.AnnotationTextColour,
                        deferredData.AnnotationsDisplayMode,
                        deferredData.ReadOnlyMode,
                        deferredData.RemoveAnnotationTitle,
                        deferredData.RemoveAnnotation,
                        deferredData.AnnotationListLocation,
                        deferredData.MarkerType,
                        deferredData.ForceImageHeight,
                        deferredData.ForceImageWidth,
                        deferredData.CanvasImagePartialID,
                        deferredData.CanvasAnnotationPartialID,
                        deferredData.HiddenAnnoJsonHolder
                    );

                    // Call the InitImgAnnoCanvas method
                    imgAnno.InitImgAnnoCanvas(data.base64String);
                })
                .catch(error => console.error('Error fetching image:', error));
        }

        /**
         * Clone the image annotation tool and replace, this is done to remove any floating events that have been registered on previous deferred uploads.
         * @param imageAnnotationHolderID the image annotation tool holder ID.
         */
        public static cloneAndReplaceImageAnnotationTool(imageAnnotationHolderID: string) {
            let imgAnnoHolder: HTMLElement = <HTMLElement>document.getElementById(imageAnnotationHolderID);

            if (imgAnnoHolder) {
                //The below steps are done to remove any floating events that have been registered on previous deferred uploads.
                //Clone the image annotation tool
                let newImgAnnoHolder = imgAnnoHolder.cloneNode(true);

                //Add the new image annotation tool before the old image annotation tool.
                imgAnnoHolder.parentElement.insertBefore(newImgAnnoHolder, imgAnnoHolder);

                //Remove the old image annotation tool.
                imgAnnoHolder.parentElement.removeChild(imgAnnoHolder);
            }
        }

        /**
         * The kendo control on a published form, errored during the upload or remove action, this handles that error.
         * @param e
         */
        public static handleKendoUploadError(e: any) {
            // Fires when the "upload" or "remove" operation fails. The Error event fires only when the Upload is in async mode.
            console.log("Error (" + e.operation + ")");
        }

        /**
         * Handle when the image is removed from the annotation tool, this will ensure that the response Json is also updated.
         * @param removeUrl - The URL to send the remove request to.
         * @param imgAnnoContainerID - The ID of the image annotation container.
         * @param uploadContainerID - The ID of the upload container.
         * @param uploadCtrlID - The ID of the upload control.
         * @param formFieldId - The ID of the form field.
         * @param formResponseJsonId - The ID of the form response JSON.
         * @param formPageId - The form page ID for the request.
         * @param resxConfirmRemovalOfImageTitle - The title for confirming the image should be removed.
         * @param resxConfirmRemovalOfImageContent - The text for confirming the image should be removed.
         * @param hiddenAnnoJsonHolder - the hidden annotation JSON holder.
         */
        public static removeImageAnnotationImageAndShowUploadTool(
            removeUrl: string,
            imgAnnoContainerID: string,
            uploadContainerID: string,
            uploadCtrlID: string,
            formFieldId: string,
            formJsonInstanceId: string,
            formPageId: string,
            resxConfirmRemovalOfImageTitle: string,
            resxConfirmRemovalOfImageContent: string,
            hiddenAnnoJsonHolder: string
        ) {
            $("<div></div>").kendoDialog({
                closable: false,
                title: resxConfirmRemovalOfImageTitle,
                content: resxConfirmRemovalOfImageContent,
                actions: [{
                    text: "Remove",
                    action: function (e) {

                        let reqVerificationToken: string = $('input:hidden[name="__RequestVerificationToken"]').val().toString();

                        let hdnRespInfoStreamId: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadStreamId_${formFieldId}`);

                        let formData = new FormData();
                        formData.append('FormFieldId', formFieldId);
                        formData.append('FormJsonInstanceId', formJsonInstanceId);
                        formData.append('FormPageId', formPageId);
                        formData.append('StreamId', hdnRespInfoStreamId.value);

                        fetch(removeUrl, {
                            method: 'POST',
                            headers: {
                                'RequestVerificationToken': reqVerificationToken
                            },
                            body: formData
                        })
                            .then(response => {

                                if (response.ok) {

                                    // Done to remove any floating event listeners
                                    HicomImageAnnotation.cloneAndReplaceImageAnnotationTool(imgAnnoContainerID);

                                    const annotationTool = document.getElementById(imgAnnoContainerID);
                                    if (annotationTool) {
                                        annotationTool.style.display = 'none';
                                        let hdnAnnJsonHolder: HTMLInputElement = <HTMLInputElement>document.getElementById(`${hiddenAnnoJsonHolder}`);


                                        if (hdnAnnJsonHolder) {
                                            hdnAnnJsonHolder.value = "";
                                        }

                                        let hdnRespInfoStreamId: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadStreamId_${formFieldId}`);
                                        let hdnRespInfoUploadedDate: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadUploadedDate_${formFieldId}`);
                                        let hdnRespInfoUploadedBy: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadUploadedBy_${formFieldId}`);
                                        let hdnRespInfoFileName: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadFileName_${formFieldId}`);
                                        let hdnRespInfoFileValueObjectId: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadFileValueObjectId_${formFieldId}`);
                                        let hdnimageAnnotationUserFileUploadStatus: HTMLInputElement = <HTMLInputElement>document.getElementById(`hdnimageAnnotationUserFileUploadStatus_${formFieldId}`);

                                        hdnRespInfoStreamId.value = "";
                                        hdnRespInfoUploadedDate.value = "";
                                        hdnRespInfoUploadedBy.value = "";
                                        hdnRespInfoFileName.value = "";
                                        hdnRespInfoFileValueObjectId.value = "";
                                        hdnimageAnnotationUserFileUploadStatus.value = "";
                                    }

                                    let uploadCtrl: kendo.ui.Upload = $(`#${uploadCtrlID}`).data("kendoUpload");
                                    uploadCtrl.clearAllFiles();

                                    const uploadTool = document.getElementById(uploadContainerID);
                                    if (uploadTool) {
                                        uploadTool.style.display = 'block';
                                    }

                                } else {
                                    // Handle error
                                    console.error('Failed to remove image');
                                }
                            })
                            .catch(error => {
                                console.error('Error:', error);
                            });
                    },
                    //@ts-ignore
                    cssClass: "btn btn-danger"
                }, {
                    text: "Cancel"
                }]
            }).data("kendoDialog").open().center();
        }

        //#endregion
    }

    /**
     * The AnnotationMarkerTypeEnum enum is used to define the type of annotation marker.
     * PLEASE NOTE: The values must match the enum in the C# code in the ImageAnnotationControlEnums.cs file
     */
    export enum AnnotationMarkerTypeEnum {
        Point = 0,
        Arrow = 1,
        Poly = 2,
        Freehand = 3,
        Circle = 4
    }

    /**
     * The AnnotationsDisplayModeEnum enum is used to define the location where the annotations will appear.
     * PLEASE NOTE: The values must match the enum in the C# code in the ImageAnnotationControlEnums.cs file
     */
    export enum AnnotationsDisplayModeEnum {
        OutsideImageInListOnly = 0,
        HoverShortTextAndInOutsideList = 1
    }

    /**
     * The AnnotationListLocationEnum enum is used to define the location where the annotation list will appear.
     * PLEASE NOTE: The values must match the enum in the C# code in the ImageAnnotationControlEnums.cs file
     */
    export enum AnnotationListLocationEnum {
        Left = 0,
        Top = 1,
        Right = 2,
        Bottom = 3
    }
}

namespace Hicom.Engine.UI.TagHelper.DataModel {

    /**
     * Represents the ImageUserUploadSuccess data model.
     */
    export class ImageUserUploadSuccessDM {

        /**
         * Indicates whether the upload was successful.
         */
        public Success: boolean;

        /**
         * The hidden control ID which holds all the details for the image annotation control.
         */
        public HiddenImgAnnoDetailsID: string;

        /**
         * The base64 encoded source of the uploaded image.
         */
        public ImageBase64Src: string;

        /**
         * The image annotation control ID.
         */
        public ImgAnnoCtrlID: string;

        /**
         * The ID of the form field associated with the image.
         */
        public FormFieldId: string;

        /**
         * The stream ID of the upload image.
         */
        public StreamId: string;

        /**
         * The name of the uploaded image.
         */
        public FileName: string;

        /**
         * The ID of the user who uploaded the image.
         */
        public UploadedBy: number;

        /**
         * The type of the uploaded image.
         */
        public FileValueObjectId: string;

        /**
         * The status of the uploaded image.
         */
        public Status: number;
    }

    /**
     * Represents a position with x and y coordinates.
     */
    export class PositionDM {
        /**
         * The x-coordinate of the position.
         */
        public X: number;

        /**
         * The y-coordinate of the position.
         */
        public Y: number;

        /**
         * Initializes a new instance of the PositionDM class.
         * @param x - The x-coordinate of the position.
         * @param y - The y-coordinate of the position.
         */
        constructor(x: number, y: number) {
            this.X = x;
            this.Y = y;
        }
    }

    /**
     * Represents a rectangular position with top-left and bottom-right coordinates.
     */
    export class RectPositionDM {
        /**
         * The top-left position of the rectangle.
         */
        TopLeft: PositionDM;

        /**
         * The bottom-right position of the rectangle.
         */
        BottomRight: PositionDM;

        /**
         * Initializes a new instance of the RectPositionDM class.
         * @param topLeft - The top-left position of the rectangle.
         * @param bottomRight - The bottom-right position of the rectangle.
         */
        constructor(topLeft: PositionDM, bottomRight: PositionDM) {
            this.TopLeft = topLeft;
            this.BottomRight = bottomRight;
        }
    }

    /**
     * Represents all image annotations data model.
     */
    export class AllImageAnnotationsDM {
        /**
         * The width of the image.
         */
        public ImageWidth: number;

        /**
         * The height of the image.
         */
        public ImageHeight: number;

        /**
         * The list of annotations.
         */
        public Annotations: Array<AnnotationDM>;

        /**
         * Initializes a new instance of the AllImageAnnotationsDM class.
         */
        constructor() {
            this.Annotations = new Array<AnnotationDM>();
        }
    }

    /**
     * Represents a single annotation data model.
     */
    export class AnnotationDM {
        /**
         * The number of the annotation.
         */
        public AnnotationNumber: number;

        /**
         * A short description of the annotation.
         */
        public AnnotationShortDesc: string;

        /**
         * A long description of the annotation.
         */
        public AnnotationLongDesc: string;

        /**
         * The list of positions that are marked by the annotation.
         */
        public MarkedPositions: Array<PositionDM>;

        /**
         * The type of the annotation marker.
         */
        public AnnotationMarkerType: AnnotationMarkerTypeEnum;

        /**
         * The location of the marker.
         */
        public MarkerLocation: RectPositionDM;

        /**
         * Initializes a new instance of the AnnotationDM class.
         * @param annotationNumber - The unique number of the annotation.
         * @param annotationShortDesc - A short description of the annotation.
         * @param annotationLongDesc - A long description of the annotation.
         * @param markedPositions - The list of positions that are marked by the annotation.
         * @param annotationMarkerType - The type of the annotation marker.
         */
        constructor(
            annotationNumber: number,
            annotationShortDesc: string,
            annotationLongDesc: string,
            markedPositions: Array<PositionDM>,
            annotationMarkerType: AnnotationMarkerTypeEnum
        ) {
            this.AnnotationNumber = annotationNumber;
            this.AnnotationShortDesc = annotationShortDesc;
            this.AnnotationLongDesc = annotationLongDesc;
            this.MarkedPositions = markedPositions;
            this.AnnotationMarkerType = annotationMarkerType;
        }
    }

}